/* istanbul ignore file */
import type { MutableRefObject } from 'react';
import { useEffect, useRef } from 'react';

import type { ProductMediaState } from 'src/components/organisms/ProductMediaCarousel/types';

const FADE_TRANSITION_MS = 150;
const UPPER_THRESHOLD = 0.9;
const LOWER_THRESHOLD = 0.6;

export type Callback = (entry: IntersectionObserverEntry) => void;
type ContainerReference = MutableRefObject<HTMLDivElement | null>;
type ItemReference = MutableRefObject<HTMLElement[]>;

// Hook to fire the given callback everytime an image comes into view on the carousel
const useSwipeIntersectionObserver = (
  callback: Callback,
  containerRef: ContainerReference,
  itemRefs: ItemReference,
  numberOfMedia: number,
) => {
  const savedCallback = useRef<null | Callback>(null);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (containerRef.current && numberOfMedia === itemRefs.current.length) {
      const observer = new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            if (
              entry.isIntersecting &&
              entry.intersectionRatio >= LOWER_THRESHOLD &&
              entry.intersectionRatio < UPPER_THRESHOLD
            ) {
              savedCallback.current!(entry);
            }
          }),
        {
          root: containerRef.current,
          threshold: LOWER_THRESHOLD,
        },
      );

      itemRefs.current.forEach((item) => observer.observe(item));

      return () => observer.disconnect();
    }
    return;
  }, [containerRef, itemRefs, numberOfMedia]);
};

// Hook to fade & scroll in the next media item if thumbnails or navigation arrows have been clicked
const useClickNavigationFadeTransition = (
  containerRef: ContainerReference,
  itemRefs: ItemReference,
  currentMedia: ProductMediaState['currentMedia'],
  navigatedBy: ProductMediaState['navigatedBy'],
) => {
  useEffect(() => {
    if (navigatedBy === 'click' && containerRef.current) {
      const container = containerRef.current;
      container.style.transition = `opacity ${FADE_TRANSITION_MS}ms`;
      container.style.opacity = '0';

      const timeoutId = setTimeout(() => {
        if (containerRef.current && itemRefs.current[currentMedia]) {
          const offsetLeftCurrentMedia = itemRefs.current[currentMedia].offsetLeft;
          containerRef.current.scrollLeft = offsetLeftCurrentMedia;
          containerRef.current.style.opacity = '1';
        }
      }, FADE_TRANSITION_MS);

      return () => clearTimeout(timeoutId);
    }
    return;
  }, [containerRef, currentMedia, itemRefs, navigatedBy]);
};

export const useProductMediaCarousel = (
  callback: Callback,
  currentMedia: ProductMediaState['currentMedia'],
  navigatedBy: ProductMediaState['navigatedBy'],
  numberOfMedia: number,
) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<HTMLElement[]>([]);

  useSwipeIntersectionObserver(callback, containerRef, itemRefs, numberOfMedia);
  useClickNavigationFadeTransition(containerRef, itemRefs, currentMedia, navigatedBy);

  return [containerRef, itemRefs] as const;
};
